import { useHistory } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import useMailboxService from 'src/modules/mailer/hooks/useMailboxService';
import {
    buildComposeBodies,
    getMailboxInboxPath,
    getMailboxSentPath,
    isValidMailboxEmail,
    mapMailboxActionError,
    parseMailboxAddressList,
} from '../../../helpers';

const useComposeMessage = ({
    t,
    discardPath,
    inReplyTo = '',
    original = null,
}) => {
    const history = useHistory();
    const service = useMailboxService();
    const resolvedDiscardPath = discardPath || getMailboxInboxPath();

    const sendMutation = useMutation({
        mutationFn: async (payload) => {
            return await service.sendMessage(payload);
        },
        onSuccess: () => {
            toast.success(t('Email sent successfully.'));
            history.push(getMailboxSentPath());
        },
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast.error(mapMailboxActionError(
                error,
                t,
                'An error occurred while sending the email.'
            ));
        },
    });

    const handleSubmit = (_event, values) => {
        const to = parseMailboxAddressList(values?.to);
        const cc = parseMailboxAddressList(values?.cc);

        if (!to.length || !to.every(isValidMailboxEmail)) {
            toast.error(t('Please enter valid email addresses, separated by commas.'));
            return;
        }

        if (cc.length && !cc.every(isValidMailboxEmail)) {
            toast.error(t('Please enter valid email addresses, separated by commas.'));
            return;
        }

        const {
            bodyText,
            bodyHtml,
        } = buildComposeBodies(values?.body, original);

        sendMutation.mutate({
            to,
            cc,
            subject: (values?.subject || '').trim(),
            bodyText,
            bodyHtml,
            ...(inReplyTo ? { inReplyTo, in_reply_to: inReplyTo } : {}),
        });
    };

    const handleDiscard = () => {
        history.push(resolvedDiscardPath);
    };

    return {
        sendMutation,
        handleSubmit,
        handleDiscard,
        isSending: sendMutation.isLoading,
    };
};

export default useComposeMessage;
